/**
 * AUDIO — sparse walking jazz bass in D minor with swing timing, plus an
 * occasional soft ride-cymbal tick. Mood, not melody: probabilistic note
 * placement around a random-walk over the scale. Owned by AUDIO.
 */
import {
  JAZZ_BASS_ATTACK_S,
  JAZZ_BASS_GAIN,
  JAZZ_BASS_LOWPASS_HZ,
  JAZZ_BASS_SCALE_HZ,
  JAZZ_BASS_SUSTAIN_RATIO,
  JAZZ_BEATS_PER_BAR,
  JAZZ_BEAT_PLAY_PROBABILITY,
  JAZZ_BEAT_S,
  JAZZ_LOOKAHEAD_S,
  JAZZ_PICKUP_PROBABILITY,
  JAZZ_RIDE_DECAY_S,
  JAZZ_RIDE_GAIN,
  JAZZ_RIDE_HIGHPASS_HZ,
  JAZZ_RIDE_PROBABILITY,
  JAZZ_SCHEDULER_INTERVAL_MS,
  JAZZ_SWING_RATIO
} from './constants'
import type { AudioEngine } from './engine'

const ROOT_INDEX = 0
const RANDOM_WALK_MAX_STEP = 2

const scaleNote = (index: number): number => {
  const clamped = Math.max(0, Math.min(JAZZ_BASS_SCALE_HZ.length - 1, index))
  return JAZZ_BASS_SCALE_HZ[clamped] ?? JAZZ_BASS_SCALE_HZ[ROOT_INDEX] ?? 0
}

const playBassNote = (
  engine: AudioEngine,
  time: number,
  freq: number,
  durationS: number
): void => {
  const osc = engine.ctx.createOscillator()
  osc.type = 'triangle'
  osc.frequency.value = freq

  const lowpass = engine.ctx.createBiquadFilter()
  lowpass.type = 'lowpass'
  lowpass.frequency.value = JAZZ_BASS_LOWPASS_HZ

  const gain = engine.ctx.createGain()
  gain.gain.setValueAtTime(0, time)
  gain.gain.linearRampToValueAtTime(JAZZ_BASS_GAIN, time + JAZZ_BASS_ATTACK_S)
  gain.gain.setValueAtTime(JAZZ_BASS_GAIN, time + durationS * JAZZ_BASS_SUSTAIN_RATIO)
  gain.gain.exponentialRampToValueAtTime(0.001, time + durationS)

  osc.connect(lowpass)
  lowpass.connect(gain)
  gain.connect(engine.master)
  osc.start(time)
  osc.stop(time + durationS)
}

const playRideTick = (engine: AudioEngine, time: number): void => {
  const source = engine.ctx.createBufferSource()
  source.buffer = engine.noiseBuffer

  const highpass = engine.ctx.createBiquadFilter()
  highpass.type = 'highpass'
  highpass.frequency.value = JAZZ_RIDE_HIGHPASS_HZ

  const gain = engine.ctx.createGain()
  gain.gain.setValueAtTime(JAZZ_RIDE_GAIN, time)
  gain.gain.exponentialRampToValueAtTime(0.0001, time + JAZZ_RIDE_DECAY_S)

  source.connect(highpass)
  highpass.connect(gain)
  gain.connect(engine.master)
  source.start(time)
  source.stop(time + JAZZ_RIDE_DECAY_S)
}

/**
 * Lookahead scheduler: every tick, schedule any beats falling inside the
 * lookahead window. Downbeats always sound; other quarters are probabilistic;
 * a swung eighth pickup occasionally follows a sounded beat.
 */
export const startJazz = (engine: AudioEngine): void => {
  let nextBeatTime = engine.ctx.currentTime + JAZZ_BEAT_S
  let beatInBar = 0
  let walkIndex = ROOT_INDEX

  const scheduleBeat = (time: number, beat: number): void => {
    const isDownbeat = beat === 0
    const sounds = isDownbeat || Math.random() < JAZZ_BEAT_PLAY_PROBABILITY

    if (sounds) {
      const step = Math.round((Math.random() * 2 - 1) * RANDOM_WALK_MAX_STEP)
      walkIndex = isDownbeat ? ROOT_INDEX : walkIndex + step
      if (walkIndex < 0 || walkIndex >= JAZZ_BASS_SCALE_HZ.length) walkIndex = ROOT_INDEX
      playBassNote(engine, time, scaleNote(walkIndex), JAZZ_BEAT_S)

      if (Math.random() < JAZZ_PICKUP_PROBABILITY) {
        const pickupIndex = walkIndex + (Math.random() < 0.5 ? 1 : -1)
        const pickupTime = time + JAZZ_BEAT_S * JAZZ_SWING_RATIO
        playBassNote(engine, pickupTime, scaleNote(pickupIndex), JAZZ_BEAT_S * (1 - JAZZ_SWING_RATIO))
      }
    }

    const isBackbeat = beat === 1 || beat === 3
    if (isBackbeat && Math.random() < JAZZ_RIDE_PROBABILITY) {
      playRideTick(engine, time)
    }
  }

  window.setInterval(() => {
    while (nextBeatTime < engine.ctx.currentTime + JAZZ_LOOKAHEAD_S) {
      scheduleBeat(nextBeatTime, beatInBar)
      nextBeatTime += JAZZ_BEAT_S
      beatInBar = (beatInBar + 1) % JAZZ_BEATS_PER_BAR
    }
  }, JAZZ_SCHEDULER_INTERVAL_MS)
}
